import {
  isRealSizeCompatible,
  PointProperties,
  RealSizeCompatible,
  ObjectScaleConfig,
  TrackModel,
  AssetInvestigationType,
} from '@customer/store/apis/investigationsApi';
import { ReactNode, useMemo } from 'react';
import { FeatureGroup, LayersControl } from 'react-leaflet';
import {
  InvestigationEnitityIdProps,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { DateTime } from 'luxon';
import { Feature, Point } from 'geojson';
import {
  InvestigationAssetMarkerProps,
  InvestigationAssetMarker,
} from '../markers/investigation-asset-marker/InvestigationAssetMarker';
import { InvestigationScaleMarker } from '../markers/investigation-scale-marker/InvestigationScaleMarker';
import { useTranslation } from 'react-i18next';
import { CustomPaneName } from '../mapConfig';
import { useAppSelector } from '@customer/store/hooks';

/** Get the most recent point that is at or before the specified time. */
export const getMostRecentPoint = (
  features: Feature<Point, PointProperties>[],
  time: number,
): Feature<Point, PointProperties> | undefined => {
  let mostRecentTimestamp: number | undefined;
  let mostRecentIndex: number | undefined;

  for (let i = 0; i < features.length; i++) {
    const point = features[i];
    const pointTime = DateTime.fromISO(point.properties.ts).toMillis();

    if (pointTime <= time) {
      if (typeof mostRecentTimestamp === 'undefined' || pointTime >= mostRecentTimestamp) {
        mostRecentIndex = i;
        mostRecentTimestamp = pointTime;
      }
    } else {
      // Stop searching for a matching point once the comparison time has been surpassed.
      break;
    }
  }

  return typeof mostRecentIndex !== 'undefined' ? features[mostRecentIndex] : undefined;
};

type GetMarkerSettingsReturn =
  | {
      isRealSizeCompatible: false;
      markerSettings: InvestigationAssetMarkerProps;
    }
  | {
      isRealSizeCompatible: true;
      markerSettings: InvestigationAssetMarkerProps & RealSizeCompatible;
    }
  | undefined;

const getMarkerSettings = (
  tracks: TrackModel[],
  assetId: number,
  time: number,
): GetMarkerSettingsReturn => {
  const assetTrack: TrackModel | undefined = tracks.find(({ asset }) => asset === assetId);

  // Exit early if there was no matching asset track or the asset track has no points.
  if (!assetTrack?.points) return undefined;

  const {
    n,
    points: { features },
    typ: type,
    obj,
    yrot,
    wid,
    len,
    boomlen,
  } = assetTrack;

  const mostRecentPoint = getMostRecentPoint(features, time);

  // Exit early if there was no matching point.
  if (!mostRecentPoint) return undefined;

  // Get the position and heading from the most recent point.
  const {
    geometry: {
      coordinates: [X, Y],
    },
    properties: { hdg: heading },
  } = mostRecentPoint;

  const objectScaleConfig: ObjectScaleConfig = {
    obj,
    yrot,
    wid,
    len,
    boomlen,
  };

  const basicSettings: InvestigationAssetMarkerProps = {
    name: n,
    type,
    position: [Y, X],
    heading,
  };

  if (isRealSizeCompatible(objectScaleConfig)) {
    return {
      isRealSizeCompatible: true,
      markerSettings: {
        ...basicSettings,
        ...objectScaleConfig,
      },
    };
  } else {
    return {
      isRealSizeCompatible: false,
      markerSettings: { ...basicSettings },
    };
  }
};

interface TracksMarkersGroupProps extends InvestigationEnitityIdProps {
  tracks: TrackModel[];
  checked?: boolean;
  pane?: CustomPaneName;
  investigationType?: AssetInvestigationType;
}

export const TracksMarkersGroup = ({
  siteId,
  investigationId,
  tracks,
  checked = true,
  pane,
  investigationType,
}: TracksMarkersGroupProps): ReactNode => {
  const { t } = useTranslation();

  const showObjectScaleMarkers = useAppSelector(
    state => state.investigationGui.showObjectScaleMarkers,
  );

  const { assetControls = {}, playback } = useInvestigationEntity({
    siteId,
    investigationId,
  });

  const visibleAssetIds = useMemo<number[]>(() => {
    return Object.entries(assetControls)
      .filter(([_, { visible }]) => visible)
      .map(([assetId]) => Number(assetId));
  }, [assetControls]);

  const time: number | undefined = playback?.currentTime;

  // Todo: introduce a useMemo of the asset settings that are not affected by time.

  if (typeof time === 'undefined') return null;

  return (
    <LayersControl.Overlay name={t('Assets')} checked={checked}>
      <FeatureGroup key="markers">
        {visibleAssetIds.map(assetId => {
          const settings = getMarkerSettings(tracks, assetId, time);

          if (settings) {
            if (settings.isRealSizeCompatible && showObjectScaleMarkers) {
              return (
                <InvestigationScaleMarker
                  key={assetId}
                  {...settings.markerSettings}
                  pane={pane}
                  investigationType={investigationType}
                />
              );
            } else {
              return (
                <InvestigationAssetMarker
                  key={assetId}
                  {...settings.markerSettings}
                  pane={pane}
                  investigationType={investigationType}
                />
              );
            }
          }

          return null;
        })}
      </FeatureGroup>
    </LayersControl.Overlay>
  );
};

export default TracksMarkersGroup;
